import React from 'react';
import { theme, st } from '../constants.js';
import { FI, FS, Chips } from '../components/FormPrimitives.js';
import { SectionHeader, Sub } from '../components/SectionLayout.js';

const TreatmentPlan = ({
  data, upd, updA,
  impairmentProfile, isProfiling, handleProfileImpairments, setImpairmentProfile,
  chatHistory, chatInput, setChatInput, chatLoading, handleChatQuery,
  diagTab, setDiagTab, aiAutoFilled,
}) => (
  <div style={{ animation: 'fadeIn 0.3s' }}>
    <div style={st.card}>
      <SectionHeader icon="📋" title="PT Treatment Plan & Priorities" subtitle="Based on all sections above — what are we going to DO?" color="#0f172a" />

      {/* ── Tab Switch ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {[['ai', '🤖 Analyze with AI'], ['manual', '✏️ Manual Entry']].map(([tab, label]) => (
          <button key={tab} type="button" onClick={() => setDiagTab(tab)} style={{
            padding: '10px 20px', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem',
            cursor: 'pointer', border: 'none', transition: 'background-color 0.15s, color 0.15s, box-shadow 0.15s',
            background: diagTab === tab ? theme.primary : '#f1f5f9',
            color: diagTab === tab ? '#fff' : theme.textMuted,
            boxShadow: diagTab === tab ? '0 2px 8px rgba(79,70,229,0.25)' : 'none',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* ══ MANUAL TAB ══ */}
      {diagTab === 'manual' && (
        <>
          {aiAutoFilled && (
            <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '12px', padding: '10px 16px', marginBottom: '20px', fontSize: '0.85rem', color: '#065f46', fontWeight: '600' }}>
              ✅ AI has pre-filled goals and precautions. You can edit them below.
            </div>
          )}

          <Sub title="🎯 PT Priorities (Ranked by Clinical Urgency)">
            <Chips label="Immediate Priorities (safety / life-threatening)" value={data.ptPriorities} onChange={v => updA('ptPriorities', v)}
              dangerOpts={['Clear life-threatening secretions', 'Prevent imminent respiratory failure', 'Address severe O₂ desaturation']}
              options={['Clear life-threatening secretions', 'Prevent imminent respiratory failure', 'Address severe O₂ desaturation', 'Prevent post-op atelectasis', 'Initiate early mobilisation', 'Reduce excessive WOB', 'Improve cough effectiveness', 'Prevent DVT / deconditioning', 'Titrate O₂ for activity', 'Patient / family education']} />
          </Sub>

          <Sub title="🩺 Treatment Approaches Selected">
            <Chips label="PT Interventions" value={data.ptTreatmentApproach} onChange={v => updA('ptTreatmentApproach', v)}
              options={['Breathing retraining (diaphragmatic)', 'Pursed-lip breathing', 'Positioning (upright / side-lying / prone)', 'Incentive spirometry', 'Active cycle of breathing technique (ACBT)', 'Huffing / forced expiration technique', 'Postural drainage', 'Manual chest techniques (percussion / vibration)', 'Assisted cough / mechanical MI-E', 'Oral / tracheal suction support', 'Graded mobilisation', 'Exercise tolerance training', 'O₂ titration during activity', 'Splinting technique (post-op)', 'Patient education (breathing / activity)', 'Falls prevention / transfer training']} />
          </Sub>

          <Sub title="📅 Goals & Progression Criteria">
            <FI label="Immediate Goals (this session / 24–48hrs)" type="textarea" value={data.ptImmediateGoals} onChange={e => upd('ptImmediateGoals', e.target.value)}
              placeholder="e.g. Tolerate sitting out of bed for 30 min; complete ACBT independently; SpO₂ stable on 2L NC during ambulation" />
            <FI label="Short-Term Goals (this admission)" type="textarea" value={data.ptShortTermGoals} onChange={e => upd('ptShortTermGoals', e.target.value)}
              placeholder="e.g. Independent secretion clearance; walk 50m with aid; wean from NC to room air at rest" />
            <FI label="Long-Term Goals (post-discharge)" type="textarea" value={data.ptLongTermGoals} onChange={e => upd('ptLongTermGoals', e.target.value)}
              placeholder="e.g. Return to pre-morbid community ambulation; manage chronic COPD independently; prevent readmission" />
            <FI label="Progression Criteria (when to advance)" type="textarea" value={data.ptProgressionCriteria} onChange={e => upd('ptProgressionCriteria', e.target.value)}
              placeholder="e.g. SpO₂ stable ≥93% on room air during activity; HR recovery <3min; Borg ≤4 at target distance" />
          </Sub>

          <Sub title="⏱️ Frequency, Precautions & Notes">
            <div style={st.grid2}>
              <FS label="PT Frequency" value={data.ptFrequency} onChange={e => upd('ptFrequency', e.target.value)}
                options={['1× daily', '2× daily', '3× daily', 'Once only (procedure)', 'PRN (as required)', 'Weekly outpatient', 'Community follow-up']} />
              <FI label="Precautions for PT" value={data.ptPrecautions} onChange={e => upd('ptPrecautions', e.target.value)} placeholder="e.g. No strenuous activity until troponin trend down; monitor HR <120; stop if SpO₂ <88%" />
            </div>
            <FI label="Clinical Plan Notes" type="textarea" value={data.planNotes} onChange={e => upd('planNotes', e.target.value)} placeholder="Clinical reasoning, coordination with team, communication to ward nurses, handover instructions..." />
          </Sub>
        </>
      )}

      {/* ══ AI TAB ══ */}
      {diagTab === 'ai' && (
      <Sub title="🤖 AI Clinical Profiling">
        {!impairmentProfile ? (
          <div className="ai-card-pre">
            <h4 className="ai-pre-title" style={{ color: '#1e40af' }}>Clinical Data Collection Complete</h4>
            <p className="ai-pre-desc">
              Run the AI Profiler to analyze all cardiopulmonary domains and generate:<br />
              <strong>Primary impairments · Complication risks · Intervention priorities · Treatment precautions · Progression criteria · Discharge goals</strong>
            </p>
            <button
              type="button"
              onClick={handleProfileImpairments}
              disabled={isProfiling}
              className="ai-analyze-btn"
              style={{ background: isProfiling ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '800', cursor: isProfiling ? 'not-allowed' : 'pointer', boxShadow: isProfiling ? 'none' : '0 10px 25px -5px rgba(79,70,229,0.4)', transition: 'all 0.2s' }}
            >
              {isProfiling ? <><span className="cp-spinner" />Analyzing...</> : '🫁 Profile Impairments & Risks ✨'}
            </button>
          </div>
        ) : (
          <div>

            {/* Header */}
            <div className="ai-results-header">
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: theme.textMain }}>🫁 Impairment & Risk Profile</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ background: '#ecfdf5', color: '#059669', padding: '8px 16px', borderRadius: '99px', fontSize: '0.85rem', fontWeight: '800', border: '1px solid #a7f3d0' }}>Cardiopulmonary</span>
                <button type="button" onClick={() => setImpairmentProfile(null)} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.textMuted, fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', padding: '4px 10px' }}>↺ Re-run</button>
              </div>
            </div>

            {/* Medical Diagnosis Banner */}
            <div style={{ backgroundColor: '#eff6ff', border: '2px solid #bfdbfe', borderRadius: '14px', padding: '18px 20px', marginBottom: '20px' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Medical Diagnosis (Doctor-Given)</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#1e40af' }}>{data.primaryDiagnosis || '—'}</div>
              {data.secondaryDiagnosis && <div style={{ fontSize: '0.82rem', color: '#3b82f6', marginTop: '4px' }}>Comorbidities: {data.secondaryDiagnosis}</div>}
            </div>

            {/* AI Disclaimer */}
            <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '10px 16px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#92400e', lineHeight: 1.55 }}>
                <strong>AI-generated content — clinical review required.</strong> This profile is produced by an AI model and may contain errors or omissions. It is intended to support, not replace, clinical judgement. Always verify findings against the patient's full clinical picture before acting.
              </p>
            </div>

            {/* Primary Impairments */}
            {impairmentProfile.primaryImpairments?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: '800', color: '#065f46' }}>⚡ Primary Physiotherapy Impairments</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {impairmentProfile.primaryImpairments.map((imp, i) => {
                    const sevColor = imp.severity === 'Severe'
                      ? { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' }
                      : imp.severity === 'Moderate'
                      ? { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' }
                      : { bg: '#d1fae5', text: '#065f46', border: '#10b981' };
                    return (
                      <div key={i} style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '14px 16px', borderLeft: `4px solid ${sevColor.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontWeight: '800', fontSize: '0.92rem', color: theme.textMain }}>{imp.domain}</span>
                          <span style={{ backgroundColor: sevColor.bg, color: sevColor.text, padding: '3px 10px', borderRadius: '99px', fontSize: '0.72rem', fontWeight: '800' }}>{imp.severity}</span>
                        </div>
                        <p style={{ margin: '0 0 6px', fontSize: '0.85rem', color: theme.textMuted, lineHeight: 1.5 }}>{imp.description}</p>
                        {imp.functionalImpact && <p style={{ margin: 0, fontSize: '0.82rem', color: '#4f46e5', fontWeight: '600' }}>→ {imp.functionalImpact}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Complication Risk Assessment */}
            {impairmentProfile.complicationRiskAssessment && Object.keys(impairmentProfile.complicationRiskAssessment).length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: '800', color: '#065f46' }}>⚠️ Complication Risk Assessment</h4>
                <div className="ai-grid-2">
                  {Object.entries(impairmentProfile.complicationRiskAssessment).map(([riskKey, risk]) => {
                    const lvl = (risk.level || '').toUpperCase();
                    const riskColor = lvl === 'HIGH' ? { bg: '#fef2f2', badge: '#ef4444', text: '#991b1b' }
                      : lvl === 'MODERATE' ? { bg: '#fffbeb', badge: '#f59e0b', text: '#92400e' }
                      : { bg: '#f0fdf4', badge: '#10b981', text: '#065f46' };
                    return (
                      <div key={riskKey} style={{ backgroundColor: riskColor.bg, borderRadius: '12px', padding: '14px', border: `1px solid ${riskColor.badge}44` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontWeight: '800', fontSize: '0.82rem', color: riskColor.text }}>{riskKey.replace(/([A-Z])/g, ' $1').replace(/Risk$/, '').trim()}</span>
                          <span style={{ backgroundColor: riskColor.badge, color: '#fff', padding: '2px 8px', borderRadius: '99px', fontSize: '0.68rem', fontWeight: '800' }}>{risk.level || '—'}</span>
                        </div>
                        {risk.contributingFactors?.length > 0 && (
                          <ul style={{ margin: '0 0 8px', paddingLeft: '16px', fontSize: '0.75rem', color: riskColor.text, lineHeight: 1.6 }}>
                            {risk.contributingFactors.map((f, fi) => <li key={fi}>{f}</li>)}
                          </ul>
                        )}
                        {risk.intervention && <p style={{ margin: 0, fontSize: '0.75rem', color: riskColor.text, fontWeight: '600' }}>→ {risk.intervention}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Functional Status */}
            {impairmentProfile.functionalStatus && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: '800', color: '#065f46' }}>🏃 Functional Status Summary</h4>
                <div style={{ backgroundColor: '#eff6ff', borderRadius: '14px', padding: '16px', border: '1px solid #bfdbfe' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                    {Object.entries(impairmentProfile.functionalStatus)
                      .filter(([k]) => k !== 'dischargeReadiness')
                      .map(([k, v]) => (
                        <div key={k} style={{ backgroundColor: '#fff', borderRadius: '10px', padding: '10px 12px' }}>
                          <div style={{ fontSize: '0.68rem', color: '#3b82f6', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>{k.replace(/([A-Z])/g, ' $1').trim()}</div>
                          <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e3a8a' }}>{v || '—'}</div>
                        </div>
                      ))}
                  </div>
                  {impairmentProfile.functionalStatus.dischargeReadiness && (
                    <div style={{ padding: '10px 14px', borderRadius: '10px', backgroundColor: impairmentProfile.functionalStatus.dischargeReadiness?.toLowerCase().includes('ready') ? '#d1fae5' : '#fee2e2', border: `1px solid ${impairmentProfile.functionalStatus.dischargeReadiness?.toLowerCase().includes('ready') ? '#6ee7b7' : '#fca5a5'}` }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '800', color: impairmentProfile.functionalStatus.dischargeReadiness?.toLowerCase().includes('ready') ? '#065f46' : '#991b1b' }}>
                        🏠 Discharge Readiness: {impairmentProfile.functionalStatus.dischargeReadiness}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Intervention Priorities */}
            {impairmentProfile.interventionPriorities?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: '800', color: '#065f46' }}>🎯 Intervention Priorities</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {impairmentProfile.interventionPriorities.map((p, i) => {
                    const pColor = i === 0 ? '#ef4444' : i === 1 ? '#f59e0b' : '#10b981';
                    return (
                      <div key={i} style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '14px 16px', borderLeft: `4px solid ${pColor}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: pColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '0.85rem', flexShrink: 0 }}>{i + 1}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '800', fontSize: '0.9rem', color: theme.textMain, marginBottom: '4px' }}>{p.domain}</div>
                          <div style={{ fontSize: '0.83rem', color: theme.textMuted, marginBottom: '4px', lineHeight: 1.5 }}>{p.intervention}</div>
                          {p.frequency && <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '4px' }}>⏱ {p.frequency}</div>}
                          {p.goal && <div style={{ fontSize: '0.82rem', color: '#4f46e5', fontWeight: '600' }}>🎯 {p.goal}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Treatment Precautions */}
            {impairmentProfile.treatmentPrecautions?.length > 0 && (
              <div style={{ marginBottom: '20px', padding: '16px 20px', borderRadius: '14px', backgroundColor: '#fef2f2', border: '2px solid #fca5a5' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem', fontWeight: '800', color: '#991b1b' }}>🚫 Treatment Precautions</h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#7f1d1d', fontSize: '0.85rem', lineHeight: 1.7 }}>
                  {impairmentProfile.treatmentPrecautions.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            )}

            {/* Progression Criteria */}
            {impairmentProfile.progressionCriteria && (
              <div style={{ marginBottom: '20px', padding: '16px 20px', borderRadius: '14px', backgroundColor: '#eff6ff', border: '2px solid #bfdbfe' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem', fontWeight: '800', color: '#1e40af' }}>📈 Progression Criteria</h4>
                {typeof impairmentProfile.progressionCriteria === 'string' ? (
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#1e3a8a', lineHeight: 1.6 }}>{impairmentProfile.progressionCriteria}</p>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#1e3a8a', fontSize: '0.85rem', lineHeight: 1.7 }}>
                    {(impairmentProfile.progressionCriteria.criteria || impairmentProfile.progressionCriteria).map?.((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                )}
              </div>
            )}

            {/* Discharge Goals */}
            {impairmentProfile.dischargeGoals?.length > 0 && (
              <div style={{ marginBottom: '20px', padding: '16px 20px', borderRadius: '14px', backgroundColor: '#f0fdf4', border: '2px solid #6ee7b7' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem', fontWeight: '800', color: '#065f46' }}>🏠 Discharge Goals</h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#065f46', fontSize: '0.85rem', lineHeight: 1.7 }}>
                  {impairmentProfile.dischargeGoals.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            )}

            {/* Patient Education */}
            {impairmentProfile.patientEducation?.length > 0 && (
              <div style={{ marginBottom: '20px', padding: '16px 20px', borderRadius: '14px', backgroundColor: '#fefce8', border: '1px solid #fde68a' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem', fontWeight: '800', color: '#92400e' }}>📚 Patient Education</h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#78350f', fontSize: '0.85rem', lineHeight: 1.7 }}>
                  {impairmentProfile.patientEducation.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            )}

            {/* Home Program */}
            {impairmentProfile.homeProgram && (impairmentProfile.homeProgram.exercises?.length > 0 || impairmentProfile.homeProgram.breathingTechniques?.length > 0 || impairmentProfile.homeProgram.activityGuidance) && (
              <div style={{ marginBottom: '20px', padding: '16px 20px', borderRadius: '14px', backgroundColor: '#f0fdf4', border: '2px solid #6ee7b7' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '0.95rem', fontWeight: '800', color: '#065f46' }}>🏠 Home Program</h4>
                {impairmentProfile.homeProgram.exercises?.length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Exercises</div>
                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#065f46', fontSize: '0.85rem', lineHeight: 1.7 }}>
                      {impairmentProfile.homeProgram.exercises.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  </div>
                )}
                {impairmentProfile.homeProgram.breathingTechniques?.length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Breathing Techniques</div>
                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#065f46', fontSize: '0.85rem', lineHeight: 1.7 }}>
                      {impairmentProfile.homeProgram.breathingTechniques.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  </div>
                )}
                {impairmentProfile.homeProgram.activityGuidance && (
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#065f46', fontStyle: 'italic' }}>🏃 {impairmentProfile.homeProgram.activityGuidance}</p>
                )}
              </div>
            )}

            {/* Outcome Measures */}
            {impairmentProfile.outcomeMeasures?.length > 0 && (
              <div style={{ marginBottom: '20px', padding: '14px 18px', borderRadius: '14px', backgroundColor: '#f8fafc', border: `1px solid ${theme.border}` }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem', fontWeight: '800', color: theme.textMain }}>📊 Outcome Measures</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {impairmentProfile.outcomeMeasures.map((item, i) => (
                    <span key={i} style={{ fontSize: '0.82rem', fontWeight: '700', padding: '4px 12px', borderRadius: '99px', background: '#e0e7ff', color: '#3730a3' }}>{item}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Chat */}
            <div style={{ marginTop: '30px', paddingTop: '25px', borderTop: `1px solid ${theme.border}` }}>
              <h4 style={{ margin: '0 0 15px', fontSize: '1.1rem', fontWeight: '800', color: theme.textMain }}>💬 Ask AI About Treatment & Progression</h4>
              <div style={{ background: '#f8fafc', borderRadius: '16px', padding: '20px', minHeight: '100px', marginBottom: '15px', border: `1px solid ${theme.border}`, maxHeight: '300px', overflowY: 'auto' }}>
                {chatHistory.length === 0 && <p style={{ margin: 0, color: theme.textMuted, fontStyle: 'italic' }}>Ask about ACBT progression, O₂ weaning, safe HR targets, or discharge planning...</p>}
                {chatHistory.map((msg, i) => (
                  <div key={i} style={{ marginBottom: '16px', lineHeight: 1.7, fontSize: '0.95rem' }}>
                    <strong style={{ display: 'block', marginBottom: '4px', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: msg.role === 'user' ? '#4f46e5' : '#059669' }}>
                      {msg.role === 'user' ? 'You' : 'PhysioAI'}
                    </strong>
                    {msg.role === 'user' ? (
                      <div style={{ background: '#eff6ff', padding: '12px 16px', borderRadius: '0 12px 12px 12px', display: 'inline-block', color: theme.textMain }}>{msg.text}</div>
                    ) : (
                      <div style={{ marginTop: '4px', color: theme.textMain, whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <input
                  style={{ ...st.input, backgroundColor: '#fff', flex: 1 }}
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  placeholder="e.g. How do I progress ACBT? When can I wean O₂? What's the safe HR target?"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleChatQuery(); } }}
                />
                <button
                  type="button"
                  onClick={handleChatQuery}
                  disabled={chatLoading || !chatInput.trim()}
                  style={{ ...st.btnPrimary, padding: '14px 24px', opacity: chatLoading || !chatInput.trim() ? 0.65 : 1, cursor: chatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer' }}
                >
                  {chatLoading ? 'Asking...' : 'Ask AI'}
                </button>
              </div>
            </div>

          </div>
        )}
      </Sub>
      )}

    </div>
  </div>
);

export default TreatmentPlan;
